import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  useAddDiaryMutation,
  useUpdateDiaryMutation
} from '@ports/internal-services/diary/diarySlice';
import { Button, Input } from '@business/workflows/ComponentUI';
import { AddDiaryModalProps, DiaryFormData } from '@business/interfaces/photo';

const AddDiaryModal: React.FC<AddDiaryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  assetId,
  userId,
  editingDiary
}) => {
  const { t } = useTranslation();
  const [isPrivate, setIsPrivate] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<DiaryFormData>();

  const [addDiary, { isLoading: isAdding }] = useAddDiaryMutation();
  const [updateDiary, { isLoading: isUpdating }] = useUpdateDiaryMutation();

  const isLoading = isAdding || isUpdating;
  const description = watch('description') || '';

  // Populate form when editing
  useEffect(() => {
    if (editingDiary) {
      setValue('title', editingDiary.title);
      setValue('description', editingDiary.description);
      setValue('isPrivate', editingDiary.isPrivate);
      setIsPrivate(editingDiary.isPrivate);
    } else {
      reset();
      setIsPrivate(false);
    }
  }, [editingDiary, isOpen, setValue, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: DiaryFormData) => {
    try {
      if (editingDiary) {
        // Update existing diary
        await updateDiary({
          id: editingDiary.id,
          updateDiary: {
            title: data.title.trim(),
            description: data.description.trim(),
            isPrivate
          }
        }).unwrap();
      } else {
        // Add new diary
        await addDiary({
          createDiary: {
            userId,
            assetId,
            title: data.title.trim(),
            description: data.description.trim(),
            isPrivate
          }
        }).unwrap();
      }

      onSave();
      reset();
    } catch (error) {
      console.error('Failed to save diary:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setIsPrivate(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {editingDiary ? t('diary.editDiary') : t('diary.addDiary')}
          </h2>
          <Button
            onClick={handleCancel}
            className="text-black hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
            btnStyle="no-outline">
            ×
          </Button>
        </div>

        {/* Subtitle */}
        <div className="px-6 pt-4 pb-2">
          <p className="text-sm text-gray-500">{t('diary.diarySubtitle')}</p>
        </div>

        {/* Content */}
        <div className="px-6 pb-4 space-y-4">
          {/* Title Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('diary.diaryTitle')}
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              {...register('title', {
                required: t('diary.titleRequired') || 'Title is required',
                maxLength: {
                  value: 100,
                  message: 'Title must be less than 100 characters'
                }
              })}
              placeholder={t('diary.diaryTitlePlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Description Textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('diary.diaryDescription')}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register('description', {
                required:
                  t('diary.descriptionRequired') || 'Description is required',
                maxLength: {
                  value: 2000,
                  message: 'Description must be less than 2000 characters'
                }
              })}
              placeholder={t('diary.diaryDescriptionPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 resize-none text-black"
              rows={6}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-500">
                {description.length}/2000
              </span>
            </div>
          </div>

          {/* Private Toggle */}
          <div className="flex items-center justify-end gap-2">
            <label
              htmlFor="private-toggle"
              className="text-sm font-medium text-gray-700">
              {t('diary.private')}
            </label>
            <Button
              id="private-toggle"
              type="button"
              btnStyle="no-outline"
              onClick={() => setIsPrivate(!isPrivate)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isPrivate ? 'bg-green-600' : 'bg-gray-300'
              }`}
              aria-pressed={isPrivate}>
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isPrivate ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t">
          <Button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            btnStyle="no-outline">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            btnStyle="no-outline"
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {editingDiary ? t('diary.editDiary') : t('diary.addDiaryButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddDiaryModal;
